import React, { useCallback, useEffect, useState } from "react";
import { getEducationalPosts } from "../../api";
import useGoToPricing from "../../hooks/useGoToPricing";
import { isLoggedIn } from "../../utils/isLoggedIn";
import { scrollPageToTop } from "../../utils/scrollPageToTop";
import Header from "../Homepage/sections/Header";
import Footer from "../Homepage/sections/Footer";
import { HomepageGlobalStyles } from "../Homepage/styles";
import MembersPagination from "../Members/MembersPagination";
import FourYearCard from "./FourYearCard";
import FourYearFeedSkeleton from "./FourYearFeedSkeleton";
import {
  FOUR_YEAR_FILTER,
  FOUR_YEAR_PAGE_SIZE,
  MODEL_PORTFOLIO_SHEET_URL,
} from "./fourYearUtils";
import {
  FourYearEmptyState,
  FourYearFeedInner,
  FourYearFeedSection,
  FourYearHero,
  FourYearHeroBgGradient,
  FourYearHeroBgRadial,
  FourYearHeroInner,
  FourYearHeroLead,
  FourYearHeroTitle,
  FourYearKicker,
  FourYearPage,
  FourYearPaginationWrap,
  FourYearPortfolioInner,
  FourYearPortfolioLead,
  FourYearPortfolioSection,
  FourYearPortfolioTitle,
  FourYearSheetButton,
  FourYearSheetFrame,
  FourYearSheetMobile,
} from "./FourYear.styles";

/**
 * Lovable shell + prod behavior: tag search "4 Year Journey", page size 2,
 * Google Sheets model portfolio embed.
 */
const FourYear = () => {
  const goToPricing = useGoToPricing();
  const loggedIn = isLoggedIn();

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [loading, setLoading] = useState(true);

  const totalPages = Math.max(1, Math.ceil(total / FOUR_YEAR_PAGE_SIZE));

  useEffect(() => {
    document.title = "4-Year Journey | bitcoin.live";
  }, []);

  const fetchPosts = useCallback(async (pageIndex = 0) => {
    setLoading(true);
    try {
      const response = await getEducationalPosts(
        FOUR_YEAR_FILTER,
        FOUR_YEAR_PAGE_SIZE,
        pageIndex
      );
      setPosts(response?.data?.items || []);
      setTotal(response?.data?.total || 0);
      setActivePage(pageIndex + 1);
    } catch (error) {
      console.error(error);
      setPosts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    scrollPageToTop();
    fetchPosts(0);
  }, [fetchPosts]);

  const handlePageChange = (pageNumber) => {
    fetchPosts(pageNumber - 1);
    scrollPageToTop();
  };

  return (
    <FourYearPage>
      <HomepageGlobalStyles />
      <Header onSubscribeClick={goToPricing} hideSpacer />

      <FourYearHero>
        <FourYearHeroBgGradient aria-hidden="true" />
        <FourYearHeroBgRadial aria-hidden="true" />
        <FourYearHeroInner>
          <FourYearKicker>Market Cycle Intelligence</FourYearKicker>
          <FourYearHeroTitle>
            4-Year <span>Journey</span>
          </FourYearHeroTitle>
          <FourYearHeroLead>
            Long-cycle market context, key milestones, and portfolio updates.
          </FourYearHeroLead>
        </FourYearHeroInner>
      </FourYearHero>

      <FourYearFeedSection>
        {loading ? (
          <FourYearFeedSkeleton />
        ) : (
          <FourYearFeedInner>
            {posts.length === 0 ? (
              <FourYearEmptyState>No Posts to Display</FourYearEmptyState>
            ) : (
              posts.map((post) => <FourYearCard key={post.id} post={post} />)
            )}
          </FourYearFeedInner>
        )}
      </FourYearFeedSection>

      {!loading && posts.length > 0 && totalPages > 1 && (
        <FourYearPaginationWrap>
          <MembersPagination
            activePage={activePage}
            totalPages={totalPages}
            onChange={handlePageChange}
          />
        </FourYearPaginationWrap>
      )}

      <FourYearPortfolioSection>
        <FourYearPortfolioInner>
          <FourYearPortfolioTitle>
            4-Year Journey Model Portfolio
          </FourYearPortfolioTitle>
          <FourYearPortfolioLead>
            Track the cycle-aligned portfolio allocations and updates.
          </FourYearPortfolioLead>

          <FourYearSheetFrame>
            <iframe
              src={MODEL_PORTFOLIO_SHEET_URL}
              title="4-Year Journey Model Portfolio"
              allowFullScreen
            />
          </FourYearSheetFrame>

          <FourYearSheetMobile>
            <FourYearSheetButton
              as="a"
              href={MODEL_PORTFOLIO_SHEET_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Model Portfolio Google Sheet
              <span className="material-icons" aria-hidden="true">
                open_in_new
              </span>
            </FourYearSheetButton>
          </FourYearSheetMobile>
        </FourYearPortfolioInner>
      </FourYearPortfolioSection>

      <Footer onSubscribeClick={goToPricing} hideSubscribeCta={loggedIn} />
    </FourYearPage>
  );
};

export default FourYear;
